import Letterhead from './Letterhead';
import { fmtDMY } from '../lib/format';

export interface MaterialsReportLine {
  complaintId: string;
  date: string;
  department: string;
  location: string;
  details: string;
  materialsUsed: Array<{ item: string; qty: number | '' }>;
  condemnedItems: Array<{ item: string; qty: number | '' }>;
  resolvedBy: string;
}

const fmtQty = (qty: number | '') => (qty !== '' && qty != null ? String(qty) : '');

/** One material/condemned line, exploded out of a complaint — each item gets its own report row. */
interface FlatLine {
  complaintId: string; date: string; department: string; location: string; details: string; resolvedBy: string;
  rowSpan: number; first: boolean;
  materialUsed: string; materialQty: string; condemnedItem: string; condemnedQty: string;
}

function flatten(rows: MaterialsReportLine[]): FlatLine[] {
  const out: FlatLine[] = [];
  for (const r of rows) {
    const lineCount = Math.max(r.materialsUsed.length, r.condemnedItems.length, 1);
    for (let i = 0; i < lineCount; i++) {
      out.push({
        complaintId: r.complaintId, date: r.date, department: r.department, location: r.location,
        details: r.details, resolvedBy: r.resolvedBy, rowSpan: lineCount, first: i === 0,
        materialUsed: r.materialsUsed[i]?.item ?? '', materialQty: r.materialsUsed[i] ? fmtQty(r.materialsUsed[i].qty) : '',
        condemnedItem: r.condemnedItems[i]?.item ?? '', condemnedQty: r.condemnedItems[i] ? fmtQty(r.condemnedItems[i].qty) : '',
      });
    }
  }
  return out;
}

/** Printable / PDF report: one row per material-used or condemned line item (not per complaint) —
 * each complaint's shared details span the rows its items occupy, the department's stock/
 * consumption record read the same way a paper ledger would. Item name and quantity are separate
 * columns so quantities can be scanned/totalled down the sheet. */
export default function MaterialsReportDoc({ from, to, rows }: { from: string; to: string; rows: MaterialsReportLine[] }) {
  const lines = flatten(rows);
  return (
    <div className="print-area a4">
      <Letterhead />
      <div className="doc-head">
        <h2 className="doc-title">Material Used &amp; Condemned Report</h2>
        <div className="doc-meta">
          <span>From: <strong>{fmtDMY(from)}</strong></span>
          <span>To: <strong>{fmtDMY(to)}</strong></span>
          <span>Complaints: <strong>{rows.length}</strong></span>
        </div>
      </div>

      {lines.length === 0 ? (
        <p className="muted">No resolved complaints with material or condemned entries in this range.</p>
      ) : (
        <div className="tableScroll">
          <table className="grid paper">
            <thead>
              <tr>
                <th rowSpan={2}>Complaint No</th>
                <th rowSpan={2}>Date</th>
                <th rowSpan={2}>Department / Location</th>
                <th rowSpan={2}>Details</th>
                <th colSpan={2} className="sec">Material Used</th>
                <th colSpan={2} className="sec">Condemned Item</th>
                <th rowSpan={2}>Resolved By</th>
              </tr>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Item</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  {l.first && <td rowSpan={l.rowSpan}>{l.complaintId}</td>}
                  {l.first && <td rowSpan={l.rowSpan}>{fmtDMY(l.date)}</td>}
                  {l.first && <td rowSpan={l.rowSpan}>{[l.department, l.location].filter(Boolean).join(' · ')}</td>}
                  {l.first && <td className="rmk" rowSpan={l.rowSpan}>{l.details}</td>}
                  <td className="rmk">{l.materialUsed || '—'}</td>
                  <td>{l.materialQty}</td>
                  <td className="rmk">{l.condemnedItem || '—'}</td>
                  <td>{l.condemnedQty}</td>
                  {l.first && <td rowSpan={l.rowSpan}>{l.resolvedBy}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="signrow"><div className="sign-ee">Electrical Engineer</div></div>
    </div>
  );
}
